<template>
  <header class="wad4-menu-v2">
    <div class="wad4-menu-v2-navbar">
      <nav class="wad4-menu__container" role="navigation" aria-label="Main Navigation">
        <ul class="wad4-menu-v2-items" role="menubar" data-nav-zone="menubar">
          <!-- Mobile burger button -->
          <li class="wad4-menu-v2-items__item wad4-menu-v2-items__item--mobile wad4-menu-v2__focusable" id="wad4-menu-v2-items__burger" @click.stop="onMenuItemClick($event, burgerMenuIndex)" @keydown="handleKeydown" tabindex="0">
            <svg v-show="!contentIsOpen || profileCardIsOpen" xmlns="http://www.w3.org/2000/svg" width="18" height="12" viewBox="0 0 18 12" fill="none">
              <path d="M17.25 10.5C17.6642 10.5 18 10.8358 18 11.25C18 11.6642 17.6642 12 17.25 12H0.75C0.335786 12 0 11.6642 0 11.25C0 10.8358 0.335786 10.5 0.75 10.5H17.25ZM17.25 5.25C17.6642 5.25 18 5.58579 18 6C18 6.41421 17.6642 6.75 17.25 6.75H0.75C0.335786 6.75 0 6.41421 0 6C0 5.58579 0.335786 5.25 0.75 5.25H17.25ZM17.25 0C17.6642 0 18 0.335786 18 0.75C18 1.16421 17.6642 1.5 17.25 1.5H0.75C0.335786 1.5 0 1.16421 0 0.75C0 0.335786 0.335786 0 0.75 0H17.25Z" fill="currentColor"/>
            </svg>
            <svg v-show="contentIsOpen && !profileCardIsOpen" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M15 3L3 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </li>
          <!-- Logo -->
          <li class="wad4-menu-v2-items__item">
            <a class="wad4-menu-v2-items__item-logo wad4-menu-v2__focusable" @keydown="handleKeydown" :href="logoHref" role="img" aria-label="Homepage"
               id="wad4MenuV2Item_logo" ref="wad4MenuV2Item_logo" tabindex="0">
              <svg width="86" height="28" viewBox="0 0 86 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <title>WAD Home Page</title>
                <path d="M46.7641 0.935547H40.514L33.6121 18.9298L26.7102 0.935547H20.4601L13.5582 18.9298L6.65084 0.935547H0.40625L10.4359 27.0697H16.6805L23.5824 9.07542L30.4898 27.0697H36.7344L43.6418 9.07542L50.5437 27.0697H56.7883L46.7641 0.935547Z" fill="#FF174D"/>
                <path d="M59.9141 0.935547V7.14179L77.7933 13.9999L59.9141 20.858V27.0697L85.8784 17.1057V10.8995V10.894L59.9141 0.935547Z" fill="#FF174D"/>
              </svg>
            </a>
          </li>
          <!-- Menu items -->
          <li
            v-for="(menuItem, index) in menuItems"
            :key="index"
            class="wad4-menu-v2-items__item wad4-menu-v2-items__item--desktop wad4-menu-v2__focusable"
            @keydown="handleKeydown"
            :id="`wad4MenuV2Item_i${index}`"
            tabindex="0"
            role="menuitem"
            @click.stop="onMenuItemClick($event, index, menuItem)"
          >
            <a v-if="(menuItem as any).directLink" class="wad4-menu-v2-items__item-label wad4-menu-v2__focusable" :class="{'wad4-menu-v2-items__item-label--active': highlightMenuItem(index)}" :href="(menuItem as any).directLink" :target="(menuItem as any).target || '_self'">
              {{ menuItem.title }}
              <svg v-if="(menuItem as any).target === '_blank'" class="wad4-menu-v2-items__item-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 19H5V5H12V3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V12H19V19ZM14 3V5H17.59L7.76 14.83L9.17 16.24L19 6.41V10H21V3H14Z" fill="currentColor"/>
              </svg>
            </a>
            <span v-else class="wad4-menu-v2-items__item-label" :class="{'wad4-menu-v2-items__item-label--active': highlightMenuItem(index)}">
              {{ menuItem.title }}
            </span>
            <div v-if="(menuItem as any).badge" class="wad4-menu-v2__badge" :class="(menuItem as any).badgeClass">{{ (menuItem as any).badge }}</div>
            <svg v-if="(menuItem as any).content" class="wad4-menu-v2-items__item-icon" :class="{'wad4-menu-v2-items__item-icon--open': activeMenuItemIndex === index}" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8 11.0008L3 6.00078L3.7 5.30078L8 9.60078L12.3 5.30078L13 6.00078L8 11.0008Z" fill="currentColor"/>
            </svg>
          </li>
          <!--
            For You. TAL-10754 retires it for portal accounts, so it is hidden for
            company and admin users and on the companies.* host altogether. It stays
            for developers on www, where wad-platform still serves public pages.
          -->
          <li v-if="showForYou" id="wad4-menu-v2-items__for-you" class="wad4-menu-v2-items__item wad4-menu-v2__focusable wad4-menu-v2-items__for-you" tabindex="0" @keydown="handleKeydown">
            <a class="wad4-menu-v2-items__for-you-link" :href="`${baseUrl}/for-you/recommended`">
              <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                <path d="M15 13.5C15.3228 13.5 15.6098 13.7064 15.7119 14.0127L16.1055 15.1963C16.1791 15.417 16.3032 15.6177 16.4678 15.7822C16.6323 15.9468 16.833 16.0709 17.0537 16.1445L18.2373 16.5381C18.5436 16.6402 18.75 16.9272 18.75 17.25C18.75 17.5728 18.5436 17.8598 18.2373 17.9619L17.0537 18.3555C16.833 18.4291 16.6323 18.5532 16.4678 18.7178C16.3032 18.8823 16.1791 19.083 16.1055 19.3037L15.7119 20.4873C15.6098 20.7936 15.3228 21 15 21C14.6772 21 14.3902 20.7936 14.2881 20.4873L13.8945 19.3037C13.8209 19.083 13.6968 18.8823 13.5322 18.7178C13.3677 18.5532 13.167 18.4291 12.9463 18.3555L11.7627 17.9619C11.4564 17.8598 11.25 17.5728 11.25 17.25C11.25 16.9272 11.4564 16.6402 11.7627 16.5381L12.9463 16.1445C13.167 16.0709 13.3677 15.9468 13.5322 15.7822C13.6968 15.6177 13.8209 15.417 13.8945 15.1963L14.2881 14.0127C14.3902 13.7064 14.6772 13.5 15 13.5ZM15 16.334C14.8832 16.5171 14.7474 16.6882 14.5928 16.8428C14.4382 16.9974 14.2671 17.1332 14.084 17.25C14.2671 17.3668 14.4382 17.5026 14.5928 17.6572C14.7472 17.8116 14.8833 17.9822 15 18.165C15.1167 17.9822 15.2528 17.8116 15.4072 17.6572C15.5616 17.5028 15.7322 17.3667 15.915 17.25C15.7322 17.1333 15.5616 16.9972 15.4072 16.8428C15.2526 16.6882 15.1168 16.5171 15 16.334ZM7.5 3C7.83478 3.00004 8.12868 3.22207 8.2207 3.54395L9.03418 6.38965C9.20929 7.00227 9.53775 7.56021 9.98828 8.01074C10.4388 8.46127 10.9968 8.78974 11.6094 8.96484L14.4561 9.7793C14.778 9.87134 15 10.1652 15 10.5C15 10.8348 14.7779 11.1287 14.4561 11.2207L11.6104 12.0342C10.9977 12.2093 10.4398 12.5377 9.98926 12.9883C9.53873 13.4388 9.21026 13.9968 9.03516 14.6094C9.03428 14.6124 9.03216 14.6151 9.03125 14.6182L8.2207 17.4561C8.12867 17.778 7.83482 18 7.5 18C7.16518 18 6.87133 17.778 6.7793 17.4561L5.96582 14.6104C5.79071 13.9977 5.46225 13.4398 5.01172 12.9893C4.56119 12.5387 4.00324 12.2103 3.39062 12.0352L0.543945 11.2207C0.222033 11.1287 -5.10373e-07 10.8348 0 10.5C3.88557e-05 10.1652 0.222071 9.87132 0.543945 9.7793L3.38965 8.96582C4.00226 8.79071 4.56021 8.46225 5.01074 8.01172C5.46127 7.56119 5.78974 7.00324 5.96484 6.39062L6.7793 3.54395L6.82227 3.42773C6.94485 3.16967 7.20709 3 7.5 3ZM7.40723 6.80273C7.16208 7.66039 6.70203 8.44152 6.07129 9.07227C5.44054 9.70301 4.65942 10.1631 3.80176 10.4082L3.47852 10.5L3.80273 10.5928C4.66039 10.8379 5.44152 11.298 6.07227 11.9287C6.70301 12.5595 7.16305 13.3406 7.4082 14.1982L7.5 14.5195L7.5918 14.1982L7.59766 14.1807C7.84395 13.3294 8.30195 12.5545 8.92871 11.9277C9.55946 11.297 10.3406 10.8369 11.1982 10.5918L11.5205 10.499L11.1973 10.4072C10.3396 10.1621 9.55848 9.70203 8.92773 9.07129C8.29699 8.44054 7.83695 7.65942 7.5918 6.80176L7.49902 6.47852L7.40723 6.80273ZM16.5 0C16.8441 0 17.144 0.234565 17.2275 0.568359L17.4863 1.60254V1.60352C17.6017 2.06505 17.8404 2.48684 18.1768 2.82324C18.5132 3.15964 18.935 3.39828 19.3965 3.51367H19.3975L20.4316 3.77246C20.7654 3.85599 21 4.15591 21 4.5C21 4.84409 20.7654 5.14401 20.4316 5.22754L19.3975 5.48633H19.3965C18.935 5.60172 18.5132 5.84036 18.1768 6.17676C17.8404 6.51316 17.6017 6.93495 17.4863 7.39648V7.39746L17.2275 8.43164C17.144 8.76544 16.8441 9 16.5 9C16.1559 9 15.856 8.76544 15.7725 8.43164L15.5137 7.39746V7.39648C15.3984 6.93515 15.1594 6.5141 14.8232 6.17773C14.4869 5.84128 14.065 5.60182 13.6035 5.48633V5.48535L12.5684 5.22754C12.2345 5.14407 12 4.84415 12 4.5C12 4.15585 12.2345 3.85593 12.5684 3.77246L13.6035 3.51367L13.7754 3.46484C14.1697 3.33717 14.529 3.11661 14.8232 2.82227C15.1594 2.4859 15.3984 2.06485 15.5137 1.60352V1.60254L15.7725 0.568359L15.8135 0.447266C15.9316 0.179015 16.199 0 16.5 0ZM16.5 3.09082C16.3277 3.37739 16.1223 3.64419 15.8838 3.88281C15.645 4.12174 15.3777 4.32749 15.0908 4.5C15.3777 4.67251 15.645 4.87826 15.8838 5.11719C16.1221 5.35561 16.3278 5.62191 16.5 5.9082C16.6723 5.62173 16.8777 5.35474 17.1162 5.11621C17.3547 4.87768 17.6217 4.6723 17.9082 4.5C17.6217 4.3277 17.3547 4.12232 17.1162 3.88379C16.8775 3.64506 16.6724 3.37757 16.5 3.09082Z" fill="currentColor"/>
              </svg>
              <span class="wad4-menu-v2-items__for-you-text">For You</span>
            </a>
          </li>
          <!-- Guest icon -->
          <li v-if="onClient && isGuest && !isAuthPending" class="wad4-menu-v2-items__item wad4-menu-v2__focusable wad4-menu-v2-items__guest">
            <button
              id="wad4-menu-v2-items__guest"
              tabindex="0"
              class="wad4-menu-v2-items__icon wad4-menu-v2__focusable"
              @keydown="handleKeydown"
              @click.stop="onMenuItemClick($event, avatarMenuIndex)"
              aria-label="Open guest card"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M11.9998 2.25C13.9767 2.25 15.9065 2.85147 17.534 3.97363C19.1615 5.0958 20.4102 6.68554 21.1131 8.5332C21.816 10.3809 21.9398 12.3992 21.4696 14.3193C20.9994 16.2391 19.9584 17.9712 18.4813 19.2852L18.4803 19.2842C16.697 20.8746 14.39 21.752 12.0008 21.749H11.9998L11.9989 21.75V21.749C9.60977 21.752 7.30348 20.8752 5.52034 19.2852L5.24788 19.0342C3.9145 17.7543 2.97099 16.1194 2.53011 14.3193C2.05987 12.3992 2.18369 10.3809 2.88655 8.5332C3.58943 6.68554 4.83818 5.0958 6.46565 3.97363C8.09316 2.85147 10.023 2.25 11.9998 2.25ZM11.9998 16.5C10.9566 16.4991 9.92709 16.7405 8.993 17.2051C8.27526 17.5621 7.62905 18.0435 7.08284 18.625C8.4985 19.6783 10.2215 20.2527 11.9989 20.25H12.0018C13.7786 20.2524 15.5014 19.6777 16.9168 18.625C16.3707 18.0437 15.7252 17.562 15.0076 17.2051C14.0736 16.7405 13.044 16.4991 12.0008 16.5H11.9998ZM11.9998 3.75C10.3271 3.75 8.69337 4.25845 7.31624 5.20801C5.93935 6.15748 4.88361 7.5032 4.2889 9.06641C3.69424 10.6297 3.58843 12.3373 3.98616 13.9619C4.32275 15.3363 5.00749 16.5963 5.96663 17.625C6.63958 16.9024 7.43678 16.3031 8.32503 15.8613C9.46671 15.2935 10.7248 14.999 11.9998 15C13.2752 14.9989 14.5336 15.2934 15.6756 15.8613C16.5637 16.303 17.3602 16.9026 18.033 17.625C18.9921 16.5963 19.6768 15.3367 20.0135 13.9619C20.4112 12.3373 20.3054 10.6297 19.7108 9.06641C19.1161 7.5032 18.0603 6.15748 16.6834 5.20801C15.3063 4.25845 13.6726 3.75 11.9998 3.75ZM14.2508 9.75C14.2508 9.15344 14.0133 8.5811 13.5916 8.15918C13.1698 7.73733 12.5974 7.50013 12.0008 7.5C11.4041 7.5 10.831 7.73722 10.409 8.15918C9.98731 8.5811 9.75081 9.15345 9.75081 9.75C9.75089 10.3466 9.98719 10.9189 10.409 11.3408C10.831 11.7628 11.4041 12 12.0008 12C12.5974 11.9999 13.1698 11.7627 13.5916 11.3408C14.0135 10.9189 14.2507 10.3466 14.2508 9.75ZM15.7508 9.75C15.7507 10.7444 15.3553 11.6982 14.6522 12.4014C13.949 13.1045 12.9952 13.4999 12.0008 13.5C11.0062 13.5 10.0517 13.1046 9.34847 12.4014C8.64534 11.6982 8.25089 10.7444 8.25081 9.75C8.25081 8.75544 8.64521 7.80092 9.34847 7.09766C10.0517 6.39445 11.0063 6 12.0008 6C12.9952 6.00013 13.949 6.39457 14.6522 7.09766C15.3554 7.80092 15.7508 8.75544 15.7508 9.75Z" fill="currentColor"/>
              </svg>
            </button>
          </li>
          <!-- Profile avatar -->
          <li
            v-if="onClient && userMappedInfo.profileCardImage"
            class="wad4-menu-v2-items__item wad4-menu-v2-items__avatar wad4-menu-v2__focusable"
            @keydown="handleKeydown"
            id="wad4-menu-v2-items__avatar"
            tabindex="0"
            @click.stop="onMenuItemClick($event, avatarMenuIndex)"
          >
            <img v-show="userMappedInfo.profileCardImage" v-bind="userMappedInfo.profileCardImage" class="wad4-menu-v2-items__item-avatar" alt="My profile image" role="img" aria-label="Profile image">
            <svg v-show="userHasNotification" class="wad4-menu-v2-items__item-notification" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="5.25" fill="#20A977" stroke="white" stroke-width="1.5"/>
            </svg>
          </li>
        </ul>
        <div id="wad4-menu-v2__skip-to-content" class="wad4-button-primary wad4-menu-v2-items__item-skip-to-content wad4-menu-v2__focusable" tabindex="0"
             @keydown="handleKeydown" @click="handleSkipClick" @blur="onSkipButtonLostFocus" autofocus>
          Skip to main content
        </div>
      </nav>
    </div>
    <div v-if="!profileCardIsOpen" class="wad4-menu-v2-overlay" :class="{'wad4-menu-v2-overlay--open': openOverlay}" @click.stop="closeMenuContent" role="presentation"></div>
    <div class="wad4-menu-v2-content" :class="{'wad4-menu-v2-content--open': contentIsOpen, 'wad4-menu-v2-content--split-reverse': activeMenuItemIndex===3, 'wad4-menu-v2-content--profile': profileCardIsOpen}"
         :id="activeMenuItemIndex !== null ? `wad4MenuV2ItemContent_i${activeMenuItemIndex}` : 'wad4MenuV2ItemContent'"
         tabindex="0" role="dialog" aria-labelledby="menu-content-title">
      <!-- Dropdown menus — rendered for any menu item with content.columns -->
      <template v-for="(menuItem, index) in menuItems" :key="'dropdown-' + index">
        <div
          v-if="(menuItem as any).content?.columns"
          v-show="activeMenuItemIndex === index"
          class="wad4-menu-v2-content__hide-on-mobile"
        >
          <div
            class="wad4-menu-v2-content__body"
            :class="(menuItem as any).content.bodyClass"
            data-nav-zone="dropdown"
          >
            <div class="wad4-menu__container wad4-menu__container--columns">
              <div
                v-for="(col, colIndex) in (menuItem as any).content.columns"
                :key="colIndex"
                class="wad4-menu-v2-content__card"
                :class="[
                  (menuItem as any).content.cardClass,
                  { 'wad4-menu-v2-content__card--highlight': colIndex === (menuItem as any).content.highlightCardIndex }
                ]"
              >
                <div class="wad4-menu-v2-content__card-title" :class="(menuItem as any).content.titleClass">{{ col.title }}</div>
                <ul class="wad4-menu-v2-content__card-list">
                  <li v-for="(item, itemIndex) in col.items" :key="itemIndex" class="wad4-menu-v2-content__card-list-item">
                    <a
                      class="wad4-menu-v2-content__card-list-link wad4-menu-v2-content__card-list-link__title wad4-menu-v2__focusable"
                      :href="item.link"
                      @keydown="handleKeydown"
                    >
                      <div class="wad4-menu-v2-content__card-list-link__wrapper wad4-menu-v2-content__card-list-link__wrapper--companies">
                        <div class="wad4-menu-v2-content__card-list-link-label--companies">
                          <div class="wad4-menu-v2-content__card-list-link-label" :class="(menuItem as any).content.linkLabelClass">
                            {{ item.title }}
                          </div>
                          <span v-if="item.badge" class="wad4-menu-v2__badge" :class="item.badgeClass">{{ item.badge }}</span>
                        </div>
                      </div>
                    </a>
                    <div v-if="item.subtitle" class="wad4-menu-v2-content__card-list-link-label--companies">
                      <div class="wad4-menu-v2-content__card-list-subtitle wad4-typo__text--sm">{{ item.subtitle }}</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- Profile card -->
      <div v-if="profileCardIsOpen" class="wad4-menu-v2-profile wad4-menu__container" :id="`wad4-menu-v2-items__dropdown_${avatarMenuIndex}`">
        <div class="wad4-menu-v2-profile-card" data-nav-zone="profile">
          <template v-if="!isGuest">
            <div class="wad4-menu-v2-profile-card__account">
              <div class="wad4-menu-v2-profile-card__account-avatar-wrapper">
                <img v-bind="userMappedInfo.profileCardImage" class="wad4-menu-v2-profile-card__account-image">
                <svg v-if="userHasNotification" class="wad4-menu-v2-items__item-notification" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <circle cx="6" cy="6" r="5.25" fill="#20A977" stroke="white" stroke-width="1.5"/>
                </svg>
              </div>
              <div class="wad4-menu-v2-profile-card__account-text">
                <span class="wad4-menu-v2-profile-card__account-title">{{ resolvedUser.full_name }}</span>
                <span class="wad4-menu-v2-profile-card__account-subtitle">{{ resolvedUser.email }}</span>
              </div>
            </div>
            <div class="wad4-menu-v2-profile-card__spacer"></div>
          </template>
          <template v-if="!props.hideThemeSwitcher">
            <div class="wad4-menu-v2-profile-card__theme-wrapper">
              <div class="wad4-menu-v2-profile-card__theme-toggle">
                <button
                  id="wad4-menu-v2-theme-light"
                  class="wad4-menu-v2-profile-card__theme-toggle-button wad4-menu-v2__focusable"
                  :class="{ 'wad4-menu-v2-profile-card__theme-toggle-button--active': resolvedTheme === 'light' }"
                  @click="setTheme('light')"
                  aria-label="Light mode"
                  tabindex="0"
                  @keydown="handleKeydown"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M11.25 21V18.75C11.25 18.3358 11.5858 18 12 18C12.4142 18 12.75 18.3358 12.75 18.75V21C12.75 21.4142 12.4142 21.75 12 21.75C11.5858 21.75 11.25 21.4142 11.25 21ZM6.69629 16.2422C6.98919 15.9493 7.46494 15.9493 7.75781 16.2422C8.05069 16.5351 8.05065 17.0108 7.75781 17.3037L6.16602 18.8945C5.8731 19.1872 5.39828 19.1873 5.10547 18.8945C4.81266 18.6017 4.81283 18.1269 5.10547 17.834L6.69629 16.2422ZM16.2422 16.2422C16.5351 15.9493 17.0108 15.9493 17.3037 16.2422L18.8945 17.834C19.1872 18.1269 19.1873 18.6017 18.8945 18.8945C18.6017 19.1873 18.1269 19.1872 17.834 18.8945L16.2422 17.3037C15.9493 17.0108 15.9493 16.5351 16.2422 16.2422ZM15 12C15 11.2044 14.6837 10.4415 14.1211 9.87891C13.5585 9.3163 12.7956 9 12 9C11.2044 9 10.4415 9.3163 9.87891 9.87891C9.3163 10.4415 9 11.2044 9 12C9 12.7956 9.3163 13.5585 9.87891 14.1211C10.4415 14.6837 11.2044 15 12 15C12.7956 15 13.5585 14.6837 14.1211 14.1211C14.6837 13.5585 15 12.7956 15 12ZM5.25 11.25C5.66421 11.25 6 11.5858 6 12C6 12.4142 5.66421 12.75 5.25 12.75H3C2.58579 12.75 2.25 12.4142 2.25 12C2.25 11.5858 2.58579 11.25 3 11.25H5.25ZM21 11.25C21.4142 11.25 21.75 11.5858 21.75 12C21.75 12.4142 21.4142 12.75 21 12.75H18.75C18.3358 12.75 18 12.4142 18 12C18 11.5858 18.3358 11.25 18.75 11.25H21ZM5.10547 5.10547C5.39828 4.81266 5.8731 4.81283 6.16602 5.10547L7.75781 6.69629C8.05066 6.98919 8.05069 7.46494 7.75781 7.75781C7.46494 8.05069 6.98919 8.05066 6.69629 7.75781L5.10547 6.16602C4.81283 5.8731 4.81266 5.39828 5.10547 5.10547ZM17.834 5.10547C18.1269 4.81283 18.6017 4.81266 18.8945 5.10547C19.1873 5.39828 19.1872 5.8731 18.8945 6.16602L17.3037 7.75781C17.0108 8.05065 16.5351 8.05069 16.2422 7.75781C15.9493 7.46494 15.9493 6.98919 16.2422 6.69629L17.834 5.10547ZM11.25 5.25V3C11.25 2.58579 11.5858 2.25 12 2.25C12.4142 2.25 12.75 2.58579 12.75 3V5.25C12.75 5.66421 12.4142 6 12 6C11.5858 6 11.25 5.66421 11.25 5.25ZM16.5 12C16.5 13.1935 16.0256 14.3377 15.1816 15.1816C14.3377 16.0256 13.1935 16.5 12 16.5C10.8065 16.5 9.66227 16.0256 8.81836 15.1816C7.97445 14.3377 7.5 13.1935 7.5 12C7.5 10.8065 7.97445 9.66227 8.81836 8.81836C9.66227 7.97445 10.8065 7.5 12 7.5C13.1935 7.5 14.3377 7.97445 15.1816 8.81836C16.0256 9.66227 16.5 10.8065 16.5 12Z" fill="currentColor"/>
                  </svg>
                </button>
                <button
                  id="wad4-menu-v2-theme-system"
                  class="wad4-menu-v2-profile-card__theme-toggle-button wad4-menu-v2__focusable"
                  :class="{ 'wad4-menu-v2-profile-card__theme-toggle-button--active': resolvedTheme === 'system' }"
                  @click="setTheme('system')"
                  aria-label="System mode"
                  tabindex="0"
                  @keydown="handleKeydown"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M14.25 18H9.75V18.2568L9.74512 18.4414C9.72401 18.8709 9.62975 19.2942 9.46484 19.6924C9.38434 19.8867 9.2861 20.0727 9.1748 20.25H14.8252C14.7139 20.0727 14.6157 19.8867 14.5352 19.6924C14.3467 19.2373 14.2499 18.7494 14.25 18.2568V18ZM20.25 14.5938C19.7974 14.8555 19.2819 15 18.75 15H5.25C4.71809 15 4.20263 14.8555 3.75 14.5938V15C3.75 15.3978 3.90815 15.7792 4.18945 16.0605C4.47076 16.3419 4.85218 16.5 5.25 16.5H18.75L18.8984 16.4922C19.2417 16.458 19.5645 16.3066 19.8105 16.0605C20.0919 15.7792 20.25 15.3978 20.25 15V14.5938ZM20.25 5.25C20.25 4.85218 20.0919 4.47076 19.8105 4.18945C19.5292 3.90815 19.1478 3.75 18.75 3.75H5.25C4.85218 3.75 4.47076 3.90815 4.18945 4.18945C3.90815 4.47076 3.75 4.85218 3.75 5.25V12C3.75 12.3978 3.90815 12.7792 4.18945 13.0605C4.47076 13.3419 4.85218 13.5 5.25 13.5H18.75C19.1478 13.5 19.5292 13.3419 19.8105 13.0605C20.0566 12.8145 20.208 12.4917 20.2422 12.1484L20.25 12V5.25ZM21.75 15C21.75 15.7956 21.4337 16.5585 20.8711 17.1211C20.3085 17.6837 19.5457 18 18.75 18H15.75V18.2568L15.7607 18.4775C15.7824 18.6974 15.8361 18.9133 15.9209 19.1182C16.034 19.3912 16.2002 19.6397 16.4092 19.8486L17.0303 20.4697C17.2448 20.6842 17.3094 21.0069 17.1934 21.2871C17.0773 21.5674 16.8033 21.75 16.5 21.75H7.5C7.19665 21.75 6.92273 21.5674 6.80664 21.2871C6.6906 21.0069 6.75525 20.6842 6.96973 20.4697L7.59082 19.8486L7.73926 19.6846C7.87935 19.5139 7.99428 19.3229 8.0791 19.1182C8.19212 18.8452 8.25004 18.5523 8.25 18.2568V18H5.25C4.45435 18 3.69152 17.6837 3.12891 17.1211C2.5663 16.5585 2.25 15.7956 2.25 15V5.25C2.25 4.45435 2.5663 3.69152 3.12891 3.12891C3.69152 2.5663 4.45435 2.25 5.25 2.25H18.75C19.5456 2.25 20.3085 2.5663 20.8711 3.12891C21.4337 3.69152 21.75 4.45435 21.75 5.25V15Z" fill="currentColor"/>
                  </svg>
                </button>
                <button
                  id="wad4-menu-v2-theme-dark"
                  class="wad4-menu-v2-profile-card__theme-toggle-button wad4-menu-v2__focusable"
                  :class="{ 'wad4-menu-v2-profile-card__theme-toggle-button--active': resolvedTheme === 'dark' }"
                  @click="setTheme('dark')"
                  aria-label="Dark mode"
                  tabindex="0"
                  @keydown="handleKeydown"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none">
                    <path d="M5.25 4.50211C5.25 3.73798 5.33305 2.9929 5.48828 2.27457C4.51227 2.9292 3.6687 3.77153 3.01074 4.75895C2.02548 6.23767 1.49985 7.97522 1.5 9.75211L1.51172 10.215C1.7526 14.9707 5.68444 18.7521 10.5 18.7521C12.2769 18.7523 14.0144 18.2266 15.4932 17.2414C16.4809 16.5832 17.3218 15.7383 17.9766 14.7619C17.2465 14.9199 16.5006 15.0021 15.75 15.0011V15.0021C9.95079 15.0021 5.25 10.3013 5.25 4.50211ZM6.75 4.50211C6.75 9.4729 10.7792 13.5021 15.75 13.5021H15.751C16.9395 13.5037 18.1168 13.2689 19.2139 12.8117C19.4943 12.695 19.8174 12.759 20.0322 12.9738C20.2471 13.1888 20.3114 13.5126 20.1943 13.7931C19.3962 15.7062 18.0493 17.34 16.3242 18.4894C14.7069 19.567 12.8248 20.1739 10.8877 20.2453L10.5 20.2521C4.70079 20.2521 2.95294e-08 15.5513 2.95294e-08 9.75211C-0.00015541 7.67926 0.613356 5.65292 1.7627 3.9279C2.91209 2.20285 4.5459 0.855889 6.45898 0.0577778C6.73959 -0.0592833 7.06334 0.00488457 7.27832 0.219887C7.4933 0.434898 7.55753 0.758625 7.44043 1.03922C6.99599 2.10426 6.75001 3.27303 6.75 4.50211Z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="wad4-menu-v2-profile-card__spacer"></div>
          </template>
          <div class="wad4-menu-v2-profile-card__menu" :class="{'wad4-menu-v2-profile-card__menu--guest': isGuest}">
            <template v-for="(item, index) in accountSpecificMenuItems" :key="index">
              <div v-if="(item as any).button" class="wad4-menu-v2-profile-card__menu-item__button">
                <a :class="(item as any).class" tabindex="0" :href="(item as any).url" @keydown="handleKeydown">
                  {{ (item as any).title }}
                  <div class="wad4-menu-v2__svg-wrapper" v-html="(item as any).svgAfter"></div>
                </a>
              </div>
              <a v-else
                 :class="{
                   'wad4-menu-v2-profile-card__menu-item--active': (item as any).active,
                   'wad4-menu-v2-profile-card__menu-item--notification': (item as any).badge,
                   'wad4-menu-v2__focusable': isGuest,
                 }"
                 tabindex="0"
                 class="wad4-menu-v2-profile-card__menu-item wad4-menu-v2__focusable"
                 :href="(item as any).url"
                 @keydown="handleKeydown"
              >
                <div class="wad4-menu-v2__svg-wrapper" v-html="(item as any).svg"></div>
                {{ (item as any).title }}
                <div class="wad4-menu-v2__svg-wrapper" v-html="(item as any).svgAfter"></div>
              </a>
              <div v-if="(item as any).divider" class="wad4-menu-v2-profile-card__spacer"></div>
            </template>
          </div>
          <template v-if="userMappedInfo.otherAccounts.length">
            <div class="wad4-menu-v2-profile-card__spacer"></div>
            <div
              v-for="(account, index) in userMappedInfo.otherAccounts"
              :key="account.type_id"
              :id="`wad4-menu-v2__profile-card_account-i${index}`"
              class="wad4-menu-v2-profile-card__account wad4-menu-v2-profile-card__account--switch wad4-menu-v2__focusable"
              @keydown="handleKeydown"
              @click.prevent="switchUser(account)"
              tabindex="0"
            >
              <img v-if="account.image" :src="`${account.image}?fit=crop&h=44&w=44`" class="wad4-menu-v2-profile-card__account-image">
              <div class="wad4-menu-v2-profile-card__account-text">
                <span class="wad4-menu-v2-profile-card__account-title">{{ account.full_name }}</span>
                <span class="wad4-menu-v2-profile-card__account-subtitle">{{ subtitleText(account) }}</span>
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9.70492 6L8.29492 7.41L12.8749 12L8.29492 16.59L9.70492 18L15.7049 12L9.70492 6Z" fill="currentColor"/>
              </svg>
            </div>
          </template>
          <template v-if="!isGuest">
            <div class="wad4-menu-v2-profile-card__spacer"></div>
            <div class="wad4-menu-v2-profile-card__footer">
              <a :href="`${baseUrl}/user/logout`" @pointerup.prevent="logout" class="wad4-menu-v2-profile-card__menu-item wad4-menu-v2__focusable"
                 @keydown="handleKeydown" id="wad4-menu-v2-items__profile-card_sign-out" tabindex="0">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.58L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z" fill="currentColor"/>
                </svg>
                Sign out
              </a>
            </div>
          </template>
        </div>
      </div>
      <!-- Mobile menu -->
      <ul v-if="activeMenuItemIndex === burgerMenuIndex" class="wad4-menu-v2-mobile wad4-menu__container" role="menu">
        <li class="wad4-menu-v2-mobile-item" v-for="(item, index) in menuItems" :key="index" role="menuitem" @keydown="burgerMenuKeydown(index, $event)">
          <a v-if="!(item as any).isExpandableOnMobile"
             class="wad4-menu-v2-mobile-link wad4-menu-v2__focusable"
             :href="(item as any).mobileLink"
             :id="`wad4-menu-burger-item__${index}`"
             :target="(item as any).target ? (item as any).target : '_self'"
             tabindex="0"
          >
            <span class="wad4-menu-v2-mobile-link__label">{{ (item as any).title }}
              <svg v-if="(item as any).target === '_blank'" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 19H5V5H12V3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V12H19V19ZM14 3V5H17.59L7.76 14.83L9.17 16.24L19 6.41V10H21V3H14Z" fill="currentColor"/>
              </svg>
              <div v-if="(item as any).badge" class="wad4-menu-v2__badge" :class="(item as any).badgeClass">{{ (item as any).badge }}</div>
            </span>
          </a>
          <div v-if="(item as any).isExpandableOnMobile"
               class="wad4-menu-v2-mobile-item-expander wad4-menu-v2__focusable"
               :id="`wad3-menu-burger-item__${index}`"
               @click="toggleDropdown(index)"
               @keydown.enter.stop.prevent="toggleDropdown(index)"
               tabindex="0"
          >
            <div class="wad4-menu-v2-mobile-item-expander__label">
              {{ (item as any).title }}
              <svg class="wad4-menu-v2-mobile-item-expander__icon" :class="{'wad4-menu-v2-mobile-item-expander__icon--rotated': mobileExpandedItem === index}" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M8 11.0008L3 6.00078L3.7 5.30078L8 9.60078L12.3 5.30078L13 6.00078L8 11.0008Z" fill="currentColor"/>
              </svg>
            </div>
          </div>
          <ul v-if="(item as any).isExpandableOnMobile"
              class="wad4-menu-v2-mobile-expandable"
              :class="{'wad4-menu-v2-mobile-expandable--open': mobileExpandedItem === index}">
            <template v-if="(item as any).content?.columns && mobileExpandedItem === index">
              <li v-for="(col, colIndex) in (item as any).content.columns" :key="colIndex" class="wad4-menu-v2-mobile-expandable-item">
                <div class="wad4-menu-v2-mobile-headings-title">{{ col.title }}</div>
                <ul class="wad4-menu-v2-mobile-headings">
                  <li v-for="(subItem, subIndex) in col.items" :key="subIndex">
                    <a class="wad4-menu-v2-mobile-link wad4-menu-v2__focusable" :href="subItem.link" :id="`wad4-menu-v2-burger-subitem__${mobileExpandedItem}${subIndex}`" :target="subItem.target ? subItem.target : '_self'">
                      <div class="wad4-menu-v2-mobile-link__label wad4-menu-v2-mobile-link__label__regular">
                        {{ subItem.title }}
                        <div v-if="subItem.badge" class="wad4-menu-v2__badge" :class="subItem.badgeClass">{{ subItem.badge }}</div>
                      </div>
                    </a>
                  </li>
                </ul>
              </li>
            </template>
            <template v-else>
              <li class="wad4-menu-v2-mobile-expandable-item" v-for="(subItem, subIndex) in (item as any).content?.items" :key="subIndex">
                <a v-if="!subItem.headings" :href="subItem.link" :class="subItem.mobile_css_class" :target="subItem.target ? subItem.target : '_self'">
                  <div :class="subItem.isButton ? 'wad4-button__text' : 'wad4-menu-v2-mobile-link__label'">
                    {{ subItem.title }}
                    <div v-if="subItem.badge" class="wad4-menu-v2__badge" :class="subItem.badgeClass">{{ subItem.badge }}</div>
                  </div>
                  <div v-if="subItem.subtitle" class="wad4-menu-v2-mobile-link__subtitle">{{ subItem.subtitle }}</div>
                </a>
                <div v-if="subItem.headings">{{ subItem.title }}</div>
                <ul v-if="subItem.headings" class="wad4-menu-v2-mobile-headings">
                  <li v-for="(heading, hIndex) in subItem.headings" :key="hIndex">
                    <a class="wad4-menu-v2-mobile-link wad4-menu-v2__focusable" :href="heading.link" :target="heading.target ? heading.target : '_self'">
                      <span class="wad4-menu-v2-mobile-link__label">{{ heading.title }}</span>
                    </a>
                  </li>
                </ul>
              </li>
            </template>
            <li v-if="(item as any).content?.button">
              <a :class="(item as any).content.button.mobile_css_class" :href="(item as any).content.button.link">
                <span class="wad4-button__text">{{ (item as any).content.button.text }}</span>
              </a>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, useAttrs } from 'vue'
import type { WadMenuProps, ThemeOption } from './types'
import { useMenuAuth, getAuthToken } from './useMenuAuth'
import { useMenuItems } from './useMenuItems'
import { useMenuTheme } from './useMenuTheme'
import { useMenuNav } from './useMenuNav'
import { useMenuRoot } from './useMenuRoot'
import { useImageCdn } from '~/composables/useImageCdn'

const props = withDefaults(defineProps<WadMenuProps>(), {
  language: 'en',
  user: undefined,
  theme: undefined,
  hideThemeSwitcher: false,
  labsBaseUrl: '',
})

const emit = defineEmits<{
  'theme-change': [theme: ThemeOption]
}>()

// --- Auth ---
const { resolvedUser, isAuthPending, isUserReady, revokeSession, clearAuthCookies } = useMenuAuth({
  apiBaseUrl: props.apiBaseUrl,
  userProp: () => props.user,
})

const isGuest = computed(() => resolvedUser.value.account_type === 'guest')
const isPortalAccount = computed(() =>
  resolvedUser.value.account_type === 'company' || resolvedUser.value.account_type === 'admin')
const onClient = ref(props.user !== undefined)

// --- Image CDN ---
const { getImageUrl, fixedWidthImageParams } = useImageCdn(props.cdnBaseUrl)

// --- State ---
const activeMenuItemIndex = ref<number | null>(null)
const mobileExpandedItem = ref<number | null>(null)
const showSkipLink = ref(false)
const tabCount = ref(0)
const loginRedirect = ref('')
const currentPath = ref('')

const avatarMenuIndex = 100 as const
const burgerMenuIndex = 150 as const

const baseUrl = computed(() => props.baseUrl)

// TAL-10764: the portal host drops the public developer navigation. Deliberately
// derived from the baseUrl prop rather than from useIsCompaniesHost(), because
// this component is also compiled into the standalone menu custom element
// (menu-ce.entry.ts), which runs on talents-frontend with no Nuxt context and so
// cannot call Nuxt composables. In the Nuxt app the two agree by construction:
// components/menu.vue passes baseUrl = useSelfBaseUrl(), which is exactly what
// useIsCompaniesHost() inspects.
const isCompaniesHost = computed(() => {
  try {
    return new URL(baseUrl.value).hostname.startsWith('companies.')
  } catch {
    return false
  }
})

// Labs runs on production www only - there is no per-environment equivalent - hence the absolute
// URL, matching how footer.vue links the legal pages. TAL-10777 made this configurable so the
// logo, the switcher and the login gate cannot drift apart on where Labs is. The constant remains
// the fallback for the custom element, which talents-frontend mounts without the attribute.
const LABS_HOME_URL = 'https://www.wearedevelopers.com'
const labsUrl = computed(() => props.labsBaseUrl || LABS_HOME_URL)

// On companies.* the logo goes to the account's own home, not to Labs.
//
// It used to point at Labs, on the reasoning that the portal root only bounces. The bounce is
// real - `/` 302s to /dashboard, which for an anonymous visitor bounces again to /login - but
// sending the logo off the portal entirely is the wrong fix: inside an admin or company session
// the logo means "back to my start", and leaving for the public site is what the separate
// WeAreDevelopers link is for (Admir, 2026-08-04). `home` is the destination the API already
// picked for this account, so admins land in /admin/dashboard rather than the company dashboard.
// Falls back to the host root when there is no home yet, which is the guest case.
// The account's own home, read from two places on purpose.
//
// When the menu is handed a `user` prop, that object is built by useAuthUser(), which maps an
// explicit field whitelist and does NOT include the top-level `home` - so reading only that
// silently fell back to the host root, which is the bounce this is meant to avoid. It does pass
// `accounts` through untouched, and every account carries its own `home` (the same field
// switchUser() already navigates to), so the current account is the reliable source here.
//
// The top-level `home` stays as the fallback for the no-prop path, where the menu fetches
// /v2/user itself and the field is present.
const currentAccountHome = computed(() => {
  const u = resolvedUser.value as { home?: string; accounts?: Array<{ current?: boolean; home?: string }> }
  return (u.accounts || []).find((a) => a.current)?.home || u.home
})

const logoHref = computed(() => {
  if(!isCompaniesHost.value) {
    return baseUrl.value
  }

  const home = currentAccountHome.value
  return home && home !== '/' ? `${baseUrl.value}${home}` : baseUrl.value
})

// TAL-10754: For You is retired for portal accounts - recommendations move to
// Labs - and TAL-10764 strips the public navigation from the portal host, so it
// goes on both counts there. Deliberately NOT gated on onClient: on www the
// server-rendered menu must keep the entry exactly as it does today, and the
// pre-hydration account_type is 'guest'. A logged-in portal user therefore sees
// it for one frame on www, the same way the avatar only appears on hydration.
const showForYou = computed(() => !isCompaniesHost.value && !isPortalAccount.value)

// --- Menu items ---
function isActiveMenuItemByRoute(pathname: string) {
  return typeof window !== 'undefined' ? window.location.pathname === pathname : false
}

const { menuItems, accountSpecificMenuItems } = useMenuItems({
  baseUrl,
  isCompaniesHost,
  labsUrl,
  user: resolvedUser,
  isActiveMenuItemByRoute,
})

// --- Theme ---
const { resolvedTheme, setTheme: setThemeInternal } = useMenuTheme({
  themeProp: () => props.theme,
  onThemeChange: (option) => emit('theme-change', option),
})

function setTheme(option: ThemeOption) {
  setThemeInternal(option)
}

// --- Computed ---
const contentIsOpen = computed(() => activeMenuItemIndex.value !== null || profileCardIsOpen.value)
const openOverlay = computed(() => activeMenuItemIndex.value !== null)
const profileCardIsOpen = computed(() => activeMenuItemIndex.value === avatarMenuIndex)
const userHasNotification = computed(() => isUserReady.value && accountSpecificMenuItems.value.filter((el: any) => el.badge).length > 0)


const userMappedInfo = computed(() => {
  if (!onClient.value) {
    return {
      profileImage: { sizes: null as any, src: null as any, srcset: null as any },
      profileCardImage: null as any,
      otherAccounts: [] as any[],
    }
  }
  const src = resolvedUser.value.image_url || getImageUrl('/public/default-avatar.png')
  return {
    profileImage: isGuest.value ? { sizes: null as any, src: null as any, srcset: null as any } : fixedWidthImageParams(src, 44, 1),
    profileCardImage: isGuest.value ? null : fixedWidthImageParams(src, 60, 1),
    otherAccounts: !isGuest.value && resolvedUser.value.accounts
      ? resolvedUser.value.accounts.filter((a: any) => a.current === false && a.type !== 'developer')
      : [],
  }
})

// --- Shadow-root-aware DOM helpers ---
const { getElementById, querySelector } = useMenuRoot()

// --- Navigation composable ---
const isMobileView = ref(false)

const { handleKeydown, burgerMenuKeydown } = useMenuNav({
  activeMenuItemIndex,
  showSkipLink,
  tabCount,
  profileCardIsOpen,
})

// --- Helpers ---
function subtitleText(account: any) {
  switch (account.type) {
    case 'developer': return 'Developer Account'
    case 'company': return 'Company Account'
    case 'admin': return 'Admin Account'
    default: return ''
  }
}

function highlightMenuItem(index: number) {
  if (activeMenuItemIndex.value !== null) {
    return activeMenuItemIndex.value === index
  }
  return !!(currentPath.value && ((menuItems.value[index] as any).activePaths || []).includes(currentPath.value))
}

function onMenuItemClick(ev: MouseEvent | PointerEvent, index: number, menuItem?: any) {
  if (menuItem?.directLink) {
    return
  }
  if (activeMenuItemIndex.value === index) {
    closeMenuContent()
  } else {
    activeMenuItemIndex.value = index
    const e = ev as any
    if (e.pointerType !== 'mouse') {
      nextTick(() => {
        if (index === burgerMenuIndex) {
          getElementById('wad4-menu-burger-item__0')?.focus()
        } else if (index === avatarMenuIndex) {
          querySelector('[data-nav-zone="profile"] .wad4-menu-v2__focusable')?.focus()
        } else {
          querySelector('[data-nav-zone="dropdown"] .wad4-menu-v2__focusable')?.focus()
        }
      })
    }
  }
}

function closeMenuContent() {
  setTimeout(() => { activeMenuItemIndex.value = null }, 100)
}

function onBodyClick(event: Event) {
  if (activeMenuItemIndex.value && profileCardIsOpen.value) {
    // In shadow DOM, event.target is retargeted to the host element. Use composedPath()
    // to get the actual clicked element so containment checks work correctly.
    const realTarget = (event as any).composedPath?.()[0] ?? event.target as Node
    const activeDropdown = querySelector(`#wad4-menu-v2-items__dropdown_${activeMenuItemIndex.value}`)
    if (activeDropdown && !activeDropdown.contains(realTarget as Node)) {
      activeMenuItemIndex.value = null
    }
  }
}

function handleSkipClick() {
  showSkipLink.value = false
  getElementById('wad4-menu-v2__skip-to-content')?.classList.remove('wad4-menu-v2-items__item-skip-to-content--show')
  nextTick(() => {
    const mainContent = document.getElementById('main-content')
    if (mainContent) {
      tabCount.value = 10;
      (mainContent as HTMLElement).focus()
    }
  })
}

function onSkipButtonLostFocus() {
  getElementById('wad4-menu-v2__skip-to-content')?.classList.remove('wad4-menu-v2-items__item-skip-to-content--show')
}

function toggleDropdown(index: number) {
  if (mobileExpandedItem.value === index) {
    mobileExpandedItem.value = null
  } else {
    mobileExpandedItem.value = null
    setTimeout(() => { mobileExpandedItem.value = index }, 10)
    getElementById(`wad4-menu-v2-burger-subitem__${index}0`)?.focus()
  }
}

async function switchUser(account: { type: string; type_id: number; home: string }) {
  try {
    const token = getAuthToken()
    await fetch(`${props.apiBaseUrl}/v2/user/accounts/default`, {
      method: 'PUT',
      headers: {
        Authorization: token ?? '',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ account_type: account.type }),
    })
    window.location.href = account.home
  } catch (err) {
    console.error('Failed to switch account', err)
  }
}

const attrs = useAttrs()

async function logout() {
  activeMenuItemIndex.value = null
  const onLogout = attrs.onLogout as (() => void) | undefined
  if (!onLogout) {
    await revokeSession()
  }
  clearAuthCookies()
  onLogout?.()
  if (!onLogout) {
    window.location.href = baseUrl.value
  }
}

function handleWindowResize() {
  isMobileView.value = window.innerWidth < 900
  if (window.innerWidth >= 900) {
    document.body.classList.remove('wad4-mobile-menu-open')
  }
}

// --- Lifecycle ---
onMounted(() => {
  onClient.value = true
  const url = new URL(window.location.href)
  const redirectParam = url.searchParams.has('redirect')
    ? url.searchParams.get('redirect')
    : (url.pathname === '/' ? null : encodeURIComponent(url.toString()))
  loginRedirect.value = redirectParam ? `?redirect=${redirectParam}` : ''
  currentPath.value = window.location.pathname
  isMobileView.value = window.innerWidth < 900

  nextTick(() => {
    const logo = getElementById('wad4MenuV2Item_logo')
    logo?.classList.add('wad4-menu-v2__no-focus-styles')
  })

  document.addEventListener('click', onBodyClick)
  window.addEventListener('resize', handleWindowResize)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onBodyClick)
  window.removeEventListener('resize', handleWindowResize)
  document.body.classList.remove('wad4-mobile-menu-open')
})

watch(showSkipLink, (val) => {
  if (val) {
    setTimeout(() => {
      const skip = getElementById('wad4-menu-v2__skip-to-content')
      if (skip) {
        skip.setAttribute('tabindex', '-1')
        skip.focus()
      }
    }, 100)
  }
})

watch(profileCardIsOpen, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      getElementById('wad4-menu-v2-theme-light')?.focus()
    })
  }
})

watch(contentIsOpen, (isOpen) => {
  if (isMobileView.value) {
    document.body.classList.toggle('wad4-mobile-menu-open', isOpen)
  }
})
</script>
